import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShopContext } from '../context/shopcontext';
import axios from 'axios';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import StarRatings from 'react-star-ratings';
import RelatedProducts from '../components/RelatedProducts';
import ProductImages from '../components/ProductImages';
import WishlistShareButtons from '../components/WishlistShareButtons';
import ProductSizeSelector from '../components/ProductSizeSelector';
import ProductPolicies from '../components/ProductPolicies';
import DescriptionReviewToggle from '../components/DescriptionReviewToggle';
import WriteReview from '../components/WriteReview';
import RecentlyViewed from '../components/RecentlyViewed';
import SkeletonProduct from '../components/SkeletonProduct';
import RelatedStyles from '../components/RelatedStyles';
import { FiX, FiMail, FiShield, FiBell, FiCheckCircle, FiChevronDown } from 'react-icons/fi';


const Product = () => {
  const { productId } = useParams();
  const { products, addToCart, backendUrl, wishlist, addToWishlist, removeFromWishlist, token } =
    useContext(ShopContext);
  const [showSizeOptions, setShowSizeOptions] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [productData, setProductData] = useState(false);
  const [size, setSize] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [showDescription, setShowDescription] = useState(true);

  const isInWishlist = wishlist.some((item) => item._id === productId);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareDescription = productData
    ? `Check out this amazing ${productData.name} - Only ₹ ${productData.price}! #ShopNow`
    : '';

  const fetchProductData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/product/${productId}`);
      if (res.data.success) {
        const product = res.data.product;
        setProductData(product);
        setCurrentImageIndex(0);

        // Fetch related products
        if (product.relatedProducts?.length) {
          try {
            const relatedProductPromises = product.relatedProducts.map(async (relatedId) => {
              const res = await axios.get(
                `${backendUrl}/api/product/get-by-productId/${relatedId}`
              );
              return res.data.success ? res.data.product : null;
            });

            const relatedResults = await Promise.all(relatedProductPromises);
            const validRelatedProducts = relatedResults.filter(Boolean);
            setRelatedProducts(validRelatedProducts);
          } catch (err) {
            console.error('Error fetching related products:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching product:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/review/${productId}/showreviews`);
      const data = response.data;

      if (data.success) {
        setReviews(data.reviews);
        setAverageRating(data.averageRating);
        setTotalReviews(data.totalReviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchProductData();
    fetchReviews();
  }, [productId]); // ✅ only depend on productId

  useEffect(() => {
    if (productId && productData && !isLoading) {
      const lastClicked = JSON.parse(sessionStorage.getItem('lastClickedTime') || '{}');
      const now = Date.now();

      if (!lastClicked[productId] || now - lastClicked[productId] > 5 * 60 * 1000) {
        axios
          .post(`${backendUrl}/api/product/click/${productId}`)
          .then(() => {
            sessionStorage.setItem(
              'lastClickedTime',
              JSON.stringify({ ...lastClicked, [productId]: now })
            );
          })
          .catch(() => {});
      }
    }
  }, [productId, productData, isLoading]);

  const handleWishlistClick = () => {
    if (!token) {
      toast.error('Please log in to use the wishlist feature.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    isInWishlist ? removeFromWishlist(productId) : addToWishlist(productId);
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!', { position: 'bottom-right', autoClose: 3000 });
      })
      .catch(() => {
        toast.error('Failed to copy link', { position: 'bottom-right', autoClose: 3000 });
      });
  };

  const handleAddToCart = () => {
    if (productData.sizes && productData.sizes.length > 0 && !size) {
      toast.error('Please select a size before adding to cart.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }
    addToCart(productData._id, size || null);
    if (!token) {
      toast.success('Product added to cart!', { position: 'bottom-right', autoClose: 3000 });
    }
  };

  // Custom Styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: '0.375rem',
      borderColor: '#D1D5DB',
      padding: '2px 4px',
      minHeight: '38px',
      boxShadow: 'none',
      '&:hover': { borderColor: '#2a081a' },
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '150px', // Approx 4-5 items
      overflowY: 'auto',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#F7E8E8' : state.isFocused ? '#F7E8E8' : 'white',
      color: state.isSelected ? '#2a081a' : 'black',
      cursor: 'pointer',
      padding: '8px 12px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
    }),
  };

  const handleNotifyMe = () => {
    setShowNotifyModal(true);
  };

  const handleNotifySubmit = async () => {
    if (!notifyEmail.trim()) {
      return toast.error('Email is required!', { position: 'bottom-right', autoClose: 3000 });
    }

    try {
      const response = await axios.post(`${backendUrl}/api/product/notifyme`, {
        productId: productData._id, // ✅ Correct
        email: notifyEmail.trim(),
        size: size || null,
      });

      if (response.data.success) {
        toast.success(response.data.message, { position: 'bottom-right', autoClose: 3000 });
        setShowNotifyModal(false);
        setNotifyEmail('');
      } else {
        toast.error(response.data.message, { position: 'bottom-right', autoClose: 3000 });
      }
    } catch (error) {
      console.error('Notify Me Error:', error);

      // If backend sends specific message
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message, { position: 'bottom-right', autoClose: 3000 });
      } else {
        toast.error('Something went wrong. Please try again later.', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    }
  };

  const preloadImage = (src) => {
    const img = new Image();
    img.src = src;
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % productData.image.length;
    preloadImage(productData.image[nextIndex]);
    setCurrentImageIndex(nextIndex);
  };

  const handlePrevImage = () => {
    const prevIndex = (currentImageIndex - 1 + productData.image.length) % productData.image.length;
    preloadImage(productData.image[prevIndex]);
    setCurrentImageIndex(prevIndex);
  };

  useEffect(() => {
    if (productData?.image) {
      productData.image.forEach((src) => preloadImage(src));
    }
  }, [productData]);

  const handleTouchStart = (e) => {
    // Ignore multi-touch gestures
    if (e.touches.length > 1) return;

    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (touchStartX === null || touchStartY === null) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;

    const deltaX = Math.abs(currentX - touchStartX);
    const deltaY = Math.abs(currentY - touchStartY);

    // If user is scrolling vertically more than horizontally, allow default scroll
    if (deltaY > deltaX && deltaY > 10) {
      setIsDragging(false);
      return; // Don't prevent default - allow scrolling
    }

    // If user is swiping horizontally more than vertically, handle image swipe
    if (deltaX > deltaY && deltaX > 10) {
      setIsDragging(true);
      // Only prevent default for horizontal swipes to change images
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    // Ignore multi-touch end
    if (e.changedTouches.length > 1 || touchStartX === null || touchStartY === null) {
      setTouchStartX(null);
      setTouchStartY(null);
      setIsDragging(false);
      return;
    }

    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;

    const deltaX = touchStartX - endX;
    const deltaY = Math.abs(endY - touchStartY);

    // Only change images if it was primarily a horizontal swipe
    if (isDragging && Math.abs(deltaX) > 50 && Math.abs(deltaX) > deltaY) {
      if (deltaX > 0) {
        handleNextImage();
      } else {
        handlePrevImage();
      }
    }

    setTouchStartX(null);
    setTouchStartY(null);
    setIsDragging(false);
  };

  return isLoading ? (
    <SkeletonProduct />
  ) : productData ? (
    <div className="border-t-2 pt-32 px-4 transition-opacity ease-in duration-500 opacity-100">
      <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 lg:gap-12 max-w-7xl mx-auto">
        {/* Left side: Images + Buttons */}
        <div className="w-full sm:w-1/2 lg:w-[45%] flex flex-col-reverse gap-3 sm:flex-row relative">
          <ProductImages
            productData={productData}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            handlePrevImage={handlePrevImage}
            handleNextImage={handleNextImage}
            handleTouchStart={handleTouchStart}
            handleTouchMove={handleTouchMove}
            handleTouchEnd={handleTouchEnd}
          />
          {showHint && (
            <p className="absolute top-2 text-center left-1/2 transform -translate-x-1/2 text-xs text-gray-800 bg-white bg-opacity-50 px-2 py-1 rounded sm:hidden">
              Tap to Zoom
            </p>
          )}

          <WishlistShareButtons
            isInWishlist={isInWishlist}
            handleWishlistClick={handleWishlistClick}
            showShareOptions={showShareOptions}
            setShowShareOptions={setShowShareOptions}
            shareUrl={shareUrl}
            shareDescription={shareDescription}
            copyLinkToClipboard={copyLinkToClipboard}
          />
        </div>

        {/* Right side: Product Info */}
        <div className="flex-1">
          <h1 className="font-lato text-3xl mt-2 text-[#2a081a]">{productData.name}</h1>

          {averageRating > 0 && (
            <div className="flex items-center mt-2 text-[#2a081a]">
              <div className="flex items-center">
                <StarRatings
                  rating={averageRating}
                  starRatedColor="#2a081a"
                  numberOfStars={5}
                  name="rating"
                  starDimension="18px"
                  starSpacing="2px"
                />
              </div>
              <span className="ml-1 mt-1 text-sm text-[#2a081a]">({totalReviews})</span>
            </div>
          )}

          <p className="mt-5 mb-5 text-2xl text-[#2a081a]">₹ {productData.price}</p>

          <ProductSizeSelector
            productData={productData}
            size={size}
            setSize={setSize}
            showSizeChart={showSizeChart}
            setShowSizeChart={setShowSizeChart}
            handleNotifyMe={handleNotifyMe}
          />

          <button
            onClick={handleAddToCart}
            disabled={
              !productData.sizes || productData.sizes.length === 0
                ? productData.stockQuantity === 0
                : productData.sizes.every((s) => s.quantity === 0)
            }
            className={`px-8 rounded-md py-3 text-sm ${
              ((!productData.sizes || productData.sizes.length === 0) &&
                productData.stockQuantity > 0) ||
              (productData.sizes && productData.sizes.some((s) => s.quantity > 0))
                ? 'text-white bg-[#2a081a] shadow-sm border border-[#f4d9c4]'
                : 'text-gray-500 bg-gray-200 cursor-not-allowed'
            }`}
          >
            {!productData.sizes || productData.sizes.length === 0
              ? productData.stockQuantity === 0
                ? 'OUT OF STOCK'
                : 'ADD TO CART'
              : productData.sizes.every((s) => s.quantity === 0)
              ? 'OUT OF STOCK'
              : 'ADD TO CART'}
          </button>
          {((!productData.sizes?.length && productData.stockQuantity === 0) ||
            (productData.sizes?.length > 0 &&
              ((!size && productData.sizes.every((s) => s.quantity === 0)) || // ✅ No size selected and all sizes are out of stock
                (size && productData.sizes.find((s) => s.size === size)?.quantity === 0)))) && ( // ✅ Size selected but out of stock
            <button
              onClick={handleNotifyMe}
              className="mt-3 ml-3 px-8 rounded-md py-3 text-sm text-white border bg-[#2a081a] hover:bg-[#2a081a] shadow-sm  "
            >
              Notify Me
            </button>
          )}

          <hr className="mt-8 sm:w-4/5" />

          <ProductPolicies productData={productData} />
        </div>
      </div>

      <RelatedStyles relatedProducts={relatedProducts} />

      <DescriptionReviewToggle
        showDescription={showDescription}
        setShowDescription={setShowDescription}
        productData={productData}
        reviews={reviews}
        totalReviews={totalReviews}
        averageRating={averageRating}
      />

      <WriteReview
        productId={productId}
        backendUrl={backendUrl}
        token={token}
        fetchReviews={fetchReviews}
      />

      <RelatedProducts
        category={productData.category}
        subCategory={productData.subCategory}
        currentProductId={productData._id}
      />
      <RecentlyViewed />
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-md shadow-xl relative max-h-[80vh] overflow-y-auto border-t-4 border-[#2a081a] animate-fadeIn">
            {/* Close Button */}
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-[#2a081a] transition-colors duration-200"
              aria-label="Close modal"
            >
              <FiX size={20} />
            </button>

            {/* Heading */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-2 text-[#2a081a]">
                <FiBell size={24} />
              </div>
              <h2 className="text-xl font-bold text-[#2a081a] mb-2">Notify Me When Available</h2>
              <p className="text-sm text-gray-600">
                We'll email you as soon as this product is back in stock.
              </p>
            </div>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleNotifySubmit();
              }}
            >
              {/* Email Input */}
              <div className="mb-4">
                <label
                  htmlFor="notifyEmail"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address <span className="text-[#2a081a]">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <FiMail size={16} />
                  </div>
                  <input
                    id="notifyEmail"
                    type="email"
                    placeholder="yourname@example.com"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md mb-1 focus:outline-none focus:ring-2 focus:ring-[#2a081a] focus:border-[#2a081a]"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 flex items-center mt-1">
                  <FiShield className="mr-1" size={12} />
                  We respect your privacy and will only use this for notifications.
                </p>
              </div>

              {/* Size Selection */}
              {/* {sizeOptions.length > 0 && (
          <div className="mb-6">
            <label htmlFor="sizeSelect" className="block text-sm font-medium text-gray-700 mb-1">
              Size <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <Select
              id="sizeSelect"
              options={sizeOptions}
              placeholder="Select Size"
              value={sizeOptions.find(option => option.value === size) || null}
              onChange={(selectedOption) => setSize(selectedOption ? selectedOption.value : '')}
              styles={customSelectStyles}
              isClearable
              menuPlacement="auto"
              menuPosition="fixed"
              components={{ DropdownIndicator: () => <FiChevronDown size={16} className="text-gray-700" /> }}
            />
          </div>
        )} */}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-3 bg-[#2a081a] text-white font-medium rounded-md hover:bg-[#2a081a] transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2a081a] shadow-sm flex items-center justify-center"
              >
                <FiBell className="mr-2" size={16} />
                Notify Me
              </button>

              {/* Success message placeholder */}
              {false && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm flex items-center">
                  <FiCheckCircle className="mr-2" size={16} />
                  You'll be notified when this product is back in stock!
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>

  ) : (
    <div className="flex justify-center items-center min-h-screen pt-20 text-[#2a081a]">
      <p className="text-xl font-semibold">Product not found.</p>
    </div>
  );
};

export default Product;
